import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecipeStore } from '../../stores/appRecipeStore';
import { AllergenImportPlan, AllergenImportSelection, allergenErrorKey, downloadAllergenWorkbook } from '../../services/recipes/recipeAllergensService';
import { canReviewAllergens } from '../../types/RecipeAllergens';
import { AllergenDeclarationSummary } from './AllergenDeclarationEditor';

interface Props { restaurantId: string; onClose: () => void; onSuccess?: () => void; onBack: () => void }

const button = 'rounded-lg border border-zinc-300 dark:border-zinc-600 px-4 py-2 disabled:opacity-50';

export default function AllergenImportReview({ restaurantId, onClose, onSuccess, onBack }: Props) {
  const { t, i18n } = useTranslation();
  const preview = useRecipeStore(s => s.previewAllergenImport);
  const commit = useRecipeStore(s => s.commitAllergenImport);
  const [file, setFile] = useState<File | null>(null);
  const [plan, setPlan] = useState<AllergenImportPlan | null>(null);
  const [selections, setSelections] = useState<Record<number, AllergenImportSelection>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState<number | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [visibleRows, setVisibleRows] = useState(20);
  const generation = useRef(0);
  // Once commit starts, retries must carry exactly the same operation and selections.
  const attempt = useRef<{ key: string; selections: AllergenImportSelection[] } | null>(null);
  useEffect(() => () => { generation.current++; }, []);
  const selectedRows = Object.values(selections);
  const duplicateTarget = new Set(selectedRows.map(row => row.recipeId)).size !== selectedRows.length;

  const download = async (kind: 'template' | 'export') => {
    setBusy(true); setError(null);
    const current = generation.current;
    try {
      const blob = await downloadAllergenWorkbook(restaurantId, kind, i18n.language.startsWith('nl') ? 'nl' : 'en');
      if (current !== generation.current) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a'); link.href = url; link.download = `recipe-allergens-${kind}.xlsx`;
      link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) { if (current === generation.current) setError(allergenErrorKey(error)); }
    finally { if (current === generation.current) setBusy(false); }
  };

  const previewFile = async () => {
    if (!file) return;
    const current = generation.current;
    setBusy(true); setError(null);
    try {
      const next = await preview(restaurantId, file);
      if (current !== generation.current) return;
      setPlan(next); setVisibleRows(20); setAcknowledged(false); attempt.current = null;
      setSelections(Object.fromEntries(next.rows.filter(row => row.recipeId && !row.errors.length &&
        (row.matchType === 'id' || row.matchType === 'exact')).map(row => [row.rowId, { rowId: row.rowId, recipeId: row.recipeId! }])));
    } catch (error) { if (current === generation.current) setError(allergenErrorKey(error)); }
    finally { if (current === generation.current) setBusy(false); }
  };

  const apply = async () => {
    if (!plan || !acknowledged || duplicateTarget || !selectedRows.length) return;
    const current = generation.current;
    attempt.current ??= { key: crypto.randomUUID(), selections: selectedRows };
    setBusy(true); setError(null);
    try {
      const count = await commit(restaurantId, plan.planId, attempt.current.key, attempt.current.selections);
      if (current === generation.current) setApplied(count);
    } catch (error) { if (current === generation.current) setError(allergenErrorKey(error)); }
    finally { if (current === generation.current) setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 dark:bg-black/50 flex items-center justify-center p-3 sm:p-6">
      <section role="dialog" aria-modal="true" aria-labelledby="allergen-import-title"
        className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-xl bg-white dark:bg-zinc-900 p-4 sm:p-6 space-y-4 text-zinc-900 dark:text-zinc-100">
        <div className="flex justify-between items-start gap-3">
          <h2 id="allergen-import-title" className="text-xl font-semibold">{t('recipeAllergens.import.title')}</h2>
          <button type="button" className={button} disabled={busy} onClick={onClose}>{t('common.close')}</button>
        </div>
        {applied !== null ? <>
          <p role="status">{t('recipeAllergens.import.applied', { count: applied })}</p>
          <button type="button" className={button} onClick={() => { onSuccess?.(); onClose(); }}>{t('common.close')}</button>
        </> : <>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">{t('recipeAllergens.import.instructions')}</p>
          {!plan ? <>
            <div className="flex flex-wrap gap-2">
              <button type="button" className={button} disabled={busy} onClick={() => download('template')}>{t('recipeAllergens.import.template')}</button>
              <button type="button" className={button} disabled={busy} onClick={() => download('export')}>{t('recipeAllergens.import.export')}</button>
            </div>
            <label className="block space-y-2">
              <span>{t('recipeAllergens.import.file')}</span>
              <input type="file" accept=".xlsx" disabled={busy} className="block w-full text-sm" onChange={event => { setFile(event.target.files?.[0] ?? null); setError(null); }} />
            </label>
            <div className="flex flex-wrap gap-2">
              <button type="button" className={button} disabled={busy} onClick={onBack}>{t('common.back')}</button>
              <button type="button" className={button} disabled={busy || !file} onClick={previewFile}>{t('recipeAllergens.import.preview')}</button>
            </div>
          </> : <>
            <p className="text-sm">{t('recipeAllergens.import.expires', { date: new Date(plan.expiresAt).toLocaleString() })}</p>
            {plan.rows.slice(0, visibleRows).map(row => {
              const selected = selections[row.rowId];
              const candidate = row.candidates.find(item => item.recipeId === selected?.recipeId);
              return <article key={row.rowId} className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-3 space-y-3">
                <h3 className="font-medium break-words">{t('recipeAllergens.import.row', { row: row.rowId })}: {row.recipeName}</h3>
                <p className="text-sm">{t(`recipeAllergens.import.match.${row.matchType}`)}</p>
                {row.errors.map((error, index) => <p key={index} className="text-sm text-red-700 dark:text-red-300">{t(allergenErrorKey(error))}</p>)}
                <label className="block space-y-1 text-sm">
                  <span>{t('recipeAllergens.import.target')}</span>
                  <select className="w-full min-w-0 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-2 py-2"
                    disabled={busy || !!attempt.current || !!row.errors.length} value={selected?.recipeId ?? ''}
                    onChange={event => {
                      const recipeId = event.target.value;
                      setAcknowledged(false);
                      setSelections(previous => { const next = { ...previous }; if (recipeId) next[row.rowId] = { rowId: row.rowId, recipeId }; else delete next[row.rowId]; return next; });
                    }}>
                    <option value="">{t('recipeAllergens.import.skip')}</option>
                    {row.candidates.map(item => <option key={item.recipeId} value={item.recipeId}>{item.name} ({item.recipeId})</option>)}
                  </select>
                </label>
                {candidate && <>
                  <details open>
                    <summary className="cursor-pointer py-2 font-medium text-sm">{t('recipeAllergens.import.compare')}</summary>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div><h4 className="font-semibold mb-2">{t('recipeAllergens.import.current')}</h4><AllergenDeclarationSummary value={candidate.current} /></div>
                      <div><h4 className="font-semibold mb-2">{t('recipeAllergens.import.proposed')}</h4><AllergenDeclarationSummary value={candidate.proposed} /></div>
                    </div>
                  </details>
                  <label className="flex items-start gap-2 text-sm">
                    <input type="checkbox" className="mt-1" disabled={busy || !!attempt.current || !canReviewAllergens(candidate.proposed)} checked={selected?.reviewed ?? false}
                      onChange={event => { setAcknowledged(false); setSelections(previous => ({ ...previous, [row.rowId]: { ...selected!, reviewed: event.target.checked } })); }} />
                    <span>{t('recipeAllergens.markReviewed')}<span className="block text-zinc-600 dark:text-zinc-400">{t('recipeAllergens.reviewRequirement')}</span></span>
                  </label>
                </>}
              </article>;
            })}
            {visibleRows < plan.rows.length && <button type="button" className={button} onClick={() => setVisibleRows(count => count + 20)}>{t('recipeAllergens.import.more')}</button>}
            {duplicateTarget && <p role="alert" className="text-red-700 dark:text-red-300">{t('recipeAllergens.errors.DUPLICATE_TARGET')}</p>}
            <label className="flex items-start gap-2 py-2 text-sm">
              <input type="checkbox" className="mt-1" checked={acknowledged} disabled={busy || !!attempt.current}
                onChange={event => setAcknowledged(event.target.checked)} />
              {t('recipeAllergens.import.acknowledge', { count: selectedRows.length })}
            </label>
            <div className="flex flex-wrap gap-2">
              {!attempt.current && <button type="button" className={button} disabled={busy} onClick={() => { setPlan(null); setError(null); }}>{t('recipeAllergens.import.changeFile')}</button>}
              <button type="button" className={button} disabled={busy || !acknowledged || duplicateTarget || !selectedRows.length} onClick={apply}>
                {t(attempt.current ? 'recipeAllergens.import.retry' : 'recipeAllergens.import.apply', { count: selectedRows.length })}
              </button>
            </div>
          </>}
          {busy && <p role="status" className="text-sm">{t('common.loading')}</p>}
          {error && <p role="alert" className="text-sm text-red-700 dark:text-red-300">{t(error)}</p>}
        </>}
      </section>
    </div>
  );
}
